import {
  type AgentMode,
  type ConversationTurnEntry,
  EventType,
  type Timestamp,
  type UserOption,
  type UUID,
} from '@/index'
import { CommandType } from '@/features/events/shared/types'
import { normalizeForComparison } from '@/common/utils'

export enum ErrorRecoveryType {
  API_ERROR_RECOVERY = 'API_ERROR_RECOVERY',
  NOTIFICATION_CONTEXT_RECOVERY = 'NOTIFICATION_CONTEXT_RECOVERY',
}

export interface Command {
  readonly commandType: CommandType
  readonly userCommandId: UUID
  readonly refEventId: UUID
  readonly timestamp: Timestamp
  readonly userOption: UserOption | null
  readonly userPrompt: string | null
  readonly agentMode: AgentMode | null
  readonly userAnswers: Record<string, unknown> | null
  readonly errorRecovery: ErrorRecoveryType | null
}

export function isInterrupt(cmd: Command): boolean {
  return cmd.commandType === CommandType.INTERRUPT_AGENT
}

export function isErrorRecovery(cmd: Command): boolean {
  return cmd.commandType === CommandType.ERROR_RECOVERY
}

export function isResume(cmd: Command): boolean {
  return cmd.commandType === CommandType.RESUME_AGENT
}

export function isPrompt(cmd: Command): boolean {
  return (
    cmd.commandType === CommandType.SUBMIT_PROMPT || cmd.commandType === CommandType.INJECT_PROMPT
  )
}

export function isUserOptionSelection(cmd: Command): boolean {
  return cmd.commandType === CommandType.SUBMIT_OPTION
}

export function isQuestionnaireAnswer(cmd: Command): boolean {
  return cmd.commandType === CommandType.SUBMIT_ANSWERS
}

export function shouldRenderInTimeline(cmd: Command): boolean {
  if (isInterrupt(cmd)) return false
  if (isResume(cmd)) return false
  if (isQuestionnaireAnswer(cmd)) return false
  if (isUserOptionSelection(cmd)) return false
  if (isErrorRecovery(cmd)) return false

  return cmd.userPrompt !== null
}

export function isDuplicateUserPromptEvent(
  event: ConversationTurnEntry,
  userCommands: readonly Command[],
): boolean {
  if (event.eventType !== EventType.USER_PROMPT) return false
  if (!event.userPrompt) return false
  const normalizedEventPrompt = normalizeForComparison(event.userPrompt)
  return userCommands.some(
    (cmd) => normalizeForComparison(cmd.userPrompt) === normalizedEventPrompt,
  )
}
